/**
 * HR & Compliance → Recruitment & Onboarding → Onboarding
 * Read-only monitoring view over the EXISTING onboarding engine.
 * Data: GET /api/admin/onboarding/overview (shared with Primary Admin data).
 * Detail: reuses AdminOnboardingPanel in readOnly mode (no Admin Override).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { RefreshCw, Search, Mail, UserPlus, FileSearch, CheckCircle2 } from 'lucide-react';
import HRLayout from '../../components/layout/HRLayout';
import AdminOnboardingPanel from '../../components/AdminOnboardingPanel';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy'); } catch { return String(iso); }
};

const STATUS_STYLES = {
  'COMPLETE': 'bg-emerald-500/15 text-emerald-500 border-emerald-500/40',
  'INCOMPLETE': 'bg-orange-500/15 text-orange-500 border-orange-500/40',
  'ACTION REQUIRED': 'bg-red-500/15 text-red-500 border-red-500/40',
  'AWAITING EMPLOYEE': 'bg-blue-500/15 text-blue-500 border-blue-500/40',
  'AWAITING REVIEW': 'bg-amber-500/15 text-amber-500 border-amber-500/40',
};

const StatusChip = ({ value, testId }) => (
  <Badge className={`${STATUS_STYLES[value] || 'bg-muted text-muted-foreground'} text-[9px] uppercase tracking-wider whitespace-nowrap rounded-sm`} data-testid={testId}>
    {value || '—'}
  </Badge>
);

const Kpi = ({ icon: Icon, label, value, testId }) => (
  <Card className="bg-card border-border rounded-sm">
    <CardContent className="p-4 flex items-center gap-3">
      <Icon className="w-5 h-5 text-purple-400 shrink-0" strokeWidth={1.75} />
      <div>
        <p className="text-2xl font-bold text-foreground" data-testid={testId}>{value}</p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function HrOnboardingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/onboarding/overview`);
      setData(res.data);
    } catch (e) {
      console.warn('Onboarding overview fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, { match: ['/onboarding', '/documents', '/job-applications', '/invite'] });

  const term = q.trim().toLowerCase();
  const matches = (s) => !term || (s || '').toLowerCase().includes(term);
  const invites = (data?.invites || []).filter((i) => matches(i.email) || matches(i.name) || matches(i.position));
  const employees = (data?.employees || []).filter(
    (e) => matches(e.name) || matches(e.email) || matches(e.employee_code) || matches(e.position)
  );
  const counts = data?.counts || {};

  return (
    <HRLayout>
      <div className="space-y-5" data-testid="hr-onboarding-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold uppercase tracking-wider text-foreground">Onboarding</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Recruitment &amp; Onboarding pipeline — invitation → registration → email verification → profile → documents → policies → terms → complete
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); fetchData(); }} className="rounded-sm" data-testid="hr-onboarding-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Mail} label="Invites Pending" value={counts.invites_pending ?? 0} testId="kpi-invites-pending" />
          <Kpi icon={UserPlus} label="In Onboarding" value={counts.in_onboarding ?? 0} testId="kpi-in-onboarding" />
          <Kpi icon={FileSearch} label="Awaiting Review" value={counts.awaiting_review ?? 0} testId="kpi-awaiting-review" />
          <Kpi icon={CheckCircle2} label="Completed (30 days)" value={counts.completed_recent ?? 0} testId="kpi-completed-recent" />
        </div>

        <div className="relative max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, code, position…"
            className="pl-9 rounded-sm border-border"
            data-testid="hr-onboarding-search"
          />
        </div>

        {/* Pending invitations */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Pending Invitations ({invites.length})
            </p>
            {invites.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="no-pending-invites">No pending invitations.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pending-invites-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3">Candidate</th>
                      <th className="py-2 pr-3">Email</th>
                      <th className="py-2 pr-3">Position</th>
                      <th className="py-2 pr-3">Sent</th>
                      <th className="py-2 pr-3">Expires</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((inv) => (
                      <tr key={inv.id} className="border-b border-border/50 last:border-0" data-testid={`invite-row-${inv.id}`}>
                        <td className="py-2 pr-3 font-medium text-foreground">{inv.name || '—'}</td>
                        <td className="py-2 pr-3 text-muted-foreground">{inv.email}</td>
                        <td className="py-2 pr-3">{inv.position || '—'}</td>
                        <td className="py-2 pr-3">{fmt(inv.created_at)}</td>
                        <td className="py-2 pr-3">{fmt(inv.expires_at)}</td>
                        <td className="py-2"><StatusChip value="AWAITING EMPLOYEE" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Employees in onboarding */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Candidates / New Employees ({employees.length})
            </p>
            {loading ? (
              <p className="text-sm text-muted-foreground italic">Loading…</p>
            ) : employees.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="no-onboarding-employees">No employees currently in onboarding.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="onboarding-employees-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3">Employee</th>
                      <th className="py-2 pr-3">Position</th>
                      <th className="py-2 pr-3">Email Verif.</th>
                      <th className="py-2 pr-3">Profile</th>
                      <th className="py-2 pr-3">Documents</th>
                      <th className="py-2 pr-3">Policies</th>
                      <th className="py-2 pr-3">Terms</th>
                      <th className="py-2">Overall</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => setSelected(e)}
                        className="border-b border-border/50 last:border-0 cursor-pointer hover:bg-accent/40 transition-colors"
                        data-testid={`onboarding-row-${e.id}`}
                      >
                        <td className="py-2.5 pr-3">
                          <p className="font-medium text-foreground">{e.name || '—'}</p>
                          <p className="text-[11px] text-muted-foreground">{e.email}{e.employee_code ? ` · #${e.employee_code}` : ''}</p>
                        </td>
                        <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{e.position || '—'}</td>
                        <td className="py-2.5 pr-3"><StatusChip value={e.email_verification_status} /></td>
                        <td className="py-2.5 pr-3"><StatusChip value={e.profile_status} /></td>
                        <td className="py-2.5 pr-3">
                          <StatusChip value={e.documents_status} />
                          {e.documents_pending_review > 0 && (
                            <p className="text-[10px] text-amber-500 mt-0.5">{e.documents_pending_review} awaiting review</p>
                          )}
                          {e.documents_rejected > 0 && (
                            <p className="text-[10px] text-red-500 mt-0.5">{e.documents_rejected} rejected</p>
                          )}
                        </td>
                        <td className="py-2.5 pr-3"><StatusChip value={e.policies_status} /></td>
                        <td className="py-2.5 pr-3"><StatusChip value={e.terms_status} /></td>
                        <td className="py-2.5"><StatusChip value={e.overall_status} testId={`overall-status-${e.id}`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail sheet — reuses the existing admin onboarding panel (read-only) */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-background" data-testid="onboarding-detail-sheet">
          {selected && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle className="uppercase tracking-wider text-sm text-left">
                  {selected.name || selected.email}
                </SheetTitle>
                <div className="flex items-center gap-2 flex-wrap text-left">
                  <StatusChip value={selected.overall_status} />
                  {selected.position && (
                    <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/40 text-[9px] uppercase tracking-wider rounded-sm">{selected.position}</Badge>
                  )}
                  {selected.employee_code && (
                    <span className="text-[11px] text-muted-foreground font-mono">#{selected.employee_code}</span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground text-left">
                  Registered {fmt(selected.registered_at)} · {selected.email}
                </p>
              </SheetHeader>
              <AdminOnboardingPanel employeeId={selected.id} readOnly onUpdated={fetchData} />
              <p className="text-[11px] text-muted-foreground mt-3 italic">
                Admin Override is reserved for the Primary Admin. Resolve missing requirements through Document Review and the normal process.
              </p>
            </>
          )}
        </SheetContent>
      </Sheet>
    </HRLayout>
  );
}
